import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function PotholeRepair() {
  const services = [
    {
      title: 'Permanent Pothole Repair',
      description: 'Hot asphalt repairs using proper techniques that last 5-10 years or longer.',
      icon: 'ri-tools-line',
    },
    {
      title: 'Emergency Repairs',
      description: 'Fast response for urgent pothole repairs that affect safety or access.',
      icon: 'ri-alert-line',
    },
    {
      title: 'Crack Repair',
      description: 'Hot rubberized crack filling to prevent potholes from forming.',
      icon: 'ri-contrast-drop-2-line',
    },
    {
      title: 'Winter Patching',
      description: 'Cold patch for temporary winter repairs, replaced with permanent hot asphalt in spring.',
      icon: 'ri-snowy-line',
    },
    {
      title: 'Multiple Pothole Repair',
      description: 'Efficient repair of multiple potholes with volume pricing discounts.',
      icon: 'ri-grid-line',
    },
    {
      title: 'Preventive Maintenance',
      description: 'Regular inspections and crack filling to prevent pothole formation.',
      icon: 'ri-shield-check-line',
    },
  ];

  const faqs = [
    {
      question: 'How much does pothole repair cost in Lincoln NE?',
      answer: 'Pothole repair costs $100-$300 per hole depending on size and depth. Small potholes (1-2 feet) cost $100-$150. Medium potholes (2-4 feet) cost $150-$250. Large potholes (4+ feet) cost $250-$400. Multiple potholes receive volume discounts. Emergency repairs may have additional fees.',
    },
    {
      question: 'How long do pothole repairs last?',
      answer: 'Properly done hot asphalt repairs last 5-10 years or longer. Cold patch is temporary (6-12 months) for winter repairs. Repair longevity depends on proper preparation, quality materials, thorough compaction, and traffic levels. We guarantee our hot asphalt repairs.',
    },
    {
      question: 'Can you repair potholes in winter?',
      answer: 'Yes! We provide year-round pothole repair. Hot asphalt requires temperatures above 50°F. In winter, we use cold patch for temporary repairs, then return in spring for permanent hot asphalt. Emergency repairs are available for safety hazards regardless of weather.',
    },
    {
      question: 'What causes potholes in Lincoln Nebraska?',
      answer: 'Lincoln\'s freeze-thaw cycles are the primary cause. Water enters cracks, freezes, expands, and breaks apart the pavement. Traffic over weakened areas creates potholes. Poor drainage, thin asphalt, and deferred maintenance accelerate pothole formation. Preventive crack filling stops 80% of potholes.',
    },
    {
      question: 'Do you offer emergency pothole repair?',
      answer: 'Yes! We provide emergency repair for safety hazards and urgent situations. Large potholes damage vehicles and create liability. We respond quickly to assess damage and implement repairs. Emergency service is available 24/7 for critical situations. Call us for urgent pothole repair needs.',
    },
    {
      question: 'Should I repair or replace my driveway?',
      answer: 'Repair if potholes are isolated and base is stable. Replace if you have multiple potholes, extensive cracking, base failure, or pavement over 20 years old. We assess your driveway and recommend the most cost-effective solution. Often repair is sufficient and costs far less than replacement.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Pothole Repair</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Pothole & Crack Repair
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Fast, permanent pothole repair and emergency asphalt services in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/57e3bf448ba147dd5910cde8486aaed3.jpg"
                alt="Lincoln NE pothole repair services"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Fast, Permanent Pothole Repairs
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Potholes are more than unsightly - they're safety hazards that damage vehicles and create liability. Our professional pothole repair services restore your pavement quickly and permanently, preventing minor problems from becoming expensive replacements.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                We specialize in permanent hot asphalt repairs that last 5-10 years or longer. Using proper techniques and quality materials, we remove damaged asphalt, prepare the base, and fill with hot asphalt that's thoroughly compacted. The result is a smooth, durable repair that blends seamlessly with existing pavement.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Serving Lincoln and Lancaster County homeowners and businesses, we provide same-day estimates and fast turnaround on most repairs. Emergency service is available for urgent situations. Our experienced crews work efficiently to restore safety and appearance.
              </p>
              <a href="tel:3084705637" className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                CALL (308) 470-5637
              </a>
            </div>
          </div>

          {/* Services Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Repair Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Professional Pothole Repair in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Potholes are inevitable in Lincoln's climate. Temperature swings from -10°F to 95°F cause asphalt expansion and contraction. Water infiltrates through cracks, freezes, expands, and breaks apart the pavement. Traffic over weakened areas creates potholes. One winter can turn small cracks into major holes that damage vehicles and create safety hazards.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Professional pothole repair is essential for safety and property value. DIY patches with cold patch from hardware stores are temporary at best - they fail within months. Proper repairs require removing damaged material, preparing the base, filling with hot asphalt, and thorough compaction. These techniques ensure lasting repairs that perform like original pavement.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Permanent Pothole Repair Process
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Quality pothole repairs start with proper preparation. We remove all loose material and debris from the hole. Leaving loose material causes new patches to fail quickly. We use shovels, brooms, and compressed air to clean thoroughly. The hole must be clean and dry for proper adhesion.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Next, we square off the edges with a saw or jackhammer. Straight, vertical edges provide better support than ragged edges. We inspect the base - if it's soft or damaged, we excavate deeper and add new base material. Proper base support is critical for lasting repairs. Weak base causes patches to sink and fail.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We apply tack coat (liquid asphalt) to the edges and bottom of the hole. Tack coat ensures strong bonding between new and existing asphalt. Then we fill with hot asphalt in layers, compacting each layer thoroughly. Proper compaction is the most important step - poorly compacted patches fail within months.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We overfill slightly to account for compaction and settling. A vibratory plate compactor or roller compacts the asphalt to proper density. The finished repair is smooth, level with surrounding pavement, and blends seamlessly. Hot asphalt repairs last 5-10 years or longer with proper technique.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              What Causes Potholes
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Understanding pothole formation helps prevent them. The process starts with small cracks in the asphalt surface. Water enters these cracks and seeps into the base material. When temperatures drop below freezing, water expands by 9%, creating pressure that breaks apart the pavement.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Repeated freeze-thaw cycles weaken the pavement structure. Lincoln experiences 40-60 freeze-thaw cycles per winter - more than enough to create potholes. Traffic over weakened areas breaks through the surface, creating holes. Once started, potholes grow rapidly as water and traffic continue the damage.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Other contributing factors: poor drainage that keeps pavement wet, thin asphalt that lacks strength, heavy vehicles that stress pavement, and deferred maintenance that allows small problems to become major failures. Preventive maintenance - especially crack filling - stops 80% of potholes before they form.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Emergency Pothole Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Large potholes are safety hazards that require immediate attention. They damage vehicles, create trip hazards, and expose property owners to liability. We provide emergency pothole repair services for urgent situations. Our crews respond quickly to assess damage and implement repairs that restore safety.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Emergency repairs may be temporary (cold patch in winter) or permanent (hot asphalt in warm weather). We work with property owners to address immediate safety concerns, then schedule permanent repairs when conditions allow. Emergency service is available 24/7 for critical situations.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Winter Pothole Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Hot asphalt repairs require temperatures above 50°F for proper bonding and compaction. In Lincoln winters, we use cold patch for temporary repairs. Cold patch is asphalt mixed with solvents that allow it to be worked in cold weather. It provides temporary fix that prevents further damage and maintains safety.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Cold patch lasts 6-12 months before needing replacement. We return in spring when temperatures allow hot asphalt repairs. We remove the cold patch, prepare the hole properly, and install permanent hot asphalt repair. This two-step approach maintains safety year-round while ensuring lasting repairs.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Crack Repair Prevents Potholes
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The best pothole repair is prevention. Cracks are the entry point for water that causes potholes. Regular crack filling prevents water infiltration and stops the freeze-thaw damage that creates potholes. A $200 crack filling service prevents $2,000 in pothole repairs.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We use hot rubberized crack filler that remains flexible through temperature extremes. Our process includes cleaning cracks with compressed air, heating filler to 350-400°F, applying with professional equipment, and smoothing for neat finish. Crack filling costs $1-$3 per linear foot and should be done every 2-3 years.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Multiple Pothole Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Driveways or parking lots with multiple potholes benefit from volume pricing. We provide discounts for repairing multiple holes in one visit. Our crews work efficiently to repair all potholes, restoring smooth, safe pavement throughout your property.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              If you have numerous potholes, extensive cracking, or widespread damage, resurfacing or replacement may be more cost-effective than individual repairs. We assess your pavement and recommend the best solution based on condition and budget. Sometimes strategic repairs are sufficient; other times complete renewal makes more sense.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Pothole Repair Cost
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Pothole repair costs $100-$300 per hole depending on size and depth. Small potholes (1-2 feet diameter, 2-4 inches deep) cost $100-$150. Medium potholes (2-4 feet diameter, 4-6 inches deep) cost $150-$250. Large potholes (4+ feet diameter, 6+ inches deep) cost $250-$400.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Multiple potholes receive volume discounts - typically 10-20% off for 3+ holes. Emergency repairs may have additional fees for urgent response. Cold patch winter repairs cost less ($50-$100) but are temporary. We provide free estimates after inspecting your potholes.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Pothole repair is cost-effective prevention. A $200 repair prevents $2,000 in vehicle damage and $5,000 in pavement replacement. It also prevents liability from injuries or vehicle damage. Timely repairs protect your investment and maintain property value.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Preventive Maintenance
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The best approach to potholes is preventing them. Our preventive maintenance programs include annual inspections, crack filling as needed, sealcoating every 2-3 years, and minor repairs before they become major problems. Regular maintenance prevents 80% of potholes and extends pavement life by years.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We create custom maintenance schedules based on pavement age, traffic, climate exposure, and condition. Scheduled maintenance budgets costs over time and ensures timely service. Property owners who maintain their pavement spend far less on repairs and replacement over the pavement's lifetime.
            </p>
          </div>

          {/* Before/After */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Repair Results
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=large%20dangerous%20pothole%20in%20asphalt%20driveway%20showing%20deep%20hole%20with%20broken%20edges%20before%20repair%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=800&height=600&seq=116&orientation=landscape"
                  alt="Before pothole repair"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <p className="text-center mt-4 text-xl font-semibold text-gray-700">Before Repair</p>
              </div>
              <div>
                <img
                  src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/c35bb0984f21240a21f60fdfadd68b85.jpeg"
                  alt="After pothole repair"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <p className="text-center mt-4 text-xl font-semibold text-gray-700">After Repair</p>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Get Your Free Repair Quote
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Fast, professional pothole repair throughout Lincoln NE
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}